import type {
  PlanAbsence,
  PlanAssignmentInfo,
  PlanBoardEmployee,
  PlanConstraintInfo,
  PlanHeadcount,
  PlanSlotInfo,
  PlanViolation,
  StaffingPlanBoard,
} from './types';
import { sortSlots } from './planningUtils';

export const cellKey = (employeeId: string, date: string) => `${employeeId}|${date}`;
export const headcountKey = (positionId: string, shiftTimeId: string, date: string) =>
  `${positionId}|${shiftTimeId}|${date}`;

const EMPLOYEE_CONSTRAINT_TYPES = new Set(['EMPLOYEE_UNAVAILABLE', 'EMPLOYEE_ONLY_SLOT', 'EMPLOYEE_NOT_SLOT']);

export type ExceptionTarget =
  | { kind: 'cell'; employeeId: string; date: string }
  | { kind: 'headcount'; positionId: string; shiftTimeId: string; date: string };

/**
 * Board places a confirmed exception applies to: employee × day cells within
 * the week, or the headcount chip for a position-headcount override.
 * PROPOSED / REJECTED exceptions do not apply to the plan, so they get none.
 */
export function exceptionTargets(c: PlanConstraintInfo, dates: string[]): ExceptionTarget[] {
  if (c.status !== 'CONFIRMED' || !c.dateFrom) return [];
  if (EMPLOYEE_CONSTRAINT_TYPES.has(c.type) && c.employeeId) {
    const from = c.dateFrom;
    const to = c.dateTo || c.dateFrom;
    return dates
      .filter((d) => d >= from && d <= to)
      .map((date) => ({ kind: 'cell' as const, employeeId: c.employeeId as string, date }));
  }
  if (c.type === 'POSITION_HEADCOUNT_OVERRIDE' && c.positionId && c.shiftTimeId && dates.includes(c.dateFrom)) {
    return [{ kind: 'headcount', positionId: c.positionId, shiftTimeId: c.shiftTimeId, date: c.dateFrom }];
  }
  return [];
}

export interface CellViolationState {
  hard: PlanViolation[];
  soft: PlanViolation[];
}

export interface PositionGroup {
  key: string;
  positionId: string | null;
  positionName: string;
  employees: PlanBoardEmployee[];
  /** Slots that have a requirement for this position, sorted by time. */
  headcountSlots: PlanSlotInfo[];
}

export interface DepartmentGroup {
  key: string;
  departmentName: string;
  positions: PositionGroup[];
}

export interface BoardModel {
  slotById: Map<string, PlanSlotInfo>;
  employeeById: Map<string, PlanBoardEmployee>;
  assignmentByCell: Map<string, PlanAssignmentInfo>;
  absenceByCell: Map<string, PlanAbsence>;
  /** Confirmed employee exceptions per cell (cellKey). */
  exceptionsByCell: Map<string, PlanConstraintInfo[]>;
  /** Confirmed headcount overrides per headcount chip (headcountKey). */
  exceptionsByHeadcount: Map<string, PlanConstraintInfo[]>;
  violationsByCell: Map<string, CellViolationState>;
  /** Violations pointing at an employee without a specific date. */
  violationsByEmployee: Map<string, CellViolationState>;
  holidayByDate: Map<string, string>;
  workDaySet: Set<string>;
  /** positionId -> shiftTimeId -> date -> headcount */
  headcount: Map<string, Map<string, Map<string, PlanHeadcount>>>;
  hardCount: number;
  softCount: number;
  sortedViolations: PlanViolation[];
  noShiftCount: number;
}

function pushViolation(map: Map<string, CellViolationState>, key: string, v: PlanViolation) {
  let entry = map.get(key);
  if (!entry) {
    entry = { hard: [], soft: [] };
    map.set(key, entry);
  }
  (v.severity === 'HARD' ? entry.hard : entry.soft).push(v);
}

export function buildBoardModel(board: StaffingPlanBoard): BoardModel {
  const slotById = new Map(board.slots.map((s) => [s.id, s]));

  const employeeById = new Map(board.employees.map((e) => [e.employeeId, e]));

  const assignmentByCell = new Map<string, PlanAssignmentInfo>();
  for (const a of board.assignments) assignmentByCell.set(cellKey(a.employeeId, a.date), a);

  const absenceByCell = new Map<string, PlanAbsence>();
  for (const a of board.absences) {
    const key = cellKey(a.employeeId, a.date);
    // An approved leave wins over a pending request on the same day.
    const existing = absenceByCell.get(key);
    if (!existing || (existing.status !== 'APPROVED' && a.status === 'APPROVED')) {
      absenceByCell.set(key, a);
    }
  }

  const exceptionsByCell = new Map<string, PlanConstraintInfo[]>();
  const exceptionsByHeadcount = new Map<string, PlanConstraintInfo[]>();
  const pushException = (map: Map<string, PlanConstraintInfo[]>, key: string, c: PlanConstraintInfo) => {
    const list = map.get(key);
    if (list) list.push(c);
    else map.set(key, [c]);
  };
  for (const c of board.constraints ?? []) {
    for (const t of exceptionTargets(c, board.dates)) {
      if (t.kind === 'cell') pushException(exceptionsByCell, cellKey(t.employeeId, t.date), c);
      else pushException(exceptionsByHeadcount, headcountKey(t.positionId, t.shiftTimeId, t.date), c);
    }
  }

  const violationsByCell = new Map<string, CellViolationState>();
  const violationsByEmployee = new Map<string, CellViolationState>();
  let hardCount = 0;
  let softCount = 0;
  for (const v of board.violations) {
    if (v.severity === 'HARD') hardCount += 1;
    else softCount += 1;
    if (v.employeeId && v.date) pushViolation(violationsByCell, cellKey(v.employeeId, v.date), v);
    else if (v.employeeId) pushViolation(violationsByEmployee, v.employeeId, v);
  }

  const sortedViolations = [...board.violations].sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'HARD' ? -1 : 1;
    return (a.date ?? '').localeCompare(b.date ?? '');
  });

  const holidayByDate = new Map(board.holidays.map((h) => [h.date, h.name]));
  const workDaySet = new Set(board.workDays);

  const headcount = new Map<string, Map<string, Map<string, PlanHeadcount>>>();
  for (const h of board.headcount) {
    let bySlot = headcount.get(h.positionId);
    if (!bySlot) {
      bySlot = new Map();
      headcount.set(h.positionId, bySlot);
    }
    let byDate = bySlot.get(h.shiftTimeId);
    if (!byDate) {
      byDate = new Map();
      bySlot.set(h.shiftTimeId, byDate);
    }
    byDate.set(h.date, h);
  }

  return {
    slotById,
    employeeById,
    assignmentByCell,
    absenceByCell,
    exceptionsByCell,
    exceptionsByHeadcount,
    violationsByCell,
    violationsByEmployee,
    holidayByDate,
    workDaySet,
    headcount,
    hardCount,
    softCount,
    sortedViolations,
    noShiftCount: board.employees.filter((e) => e.allowedSlotIds.length === 0).length,
  };
}

/**
 * Groups employees (already sorted by the API: department → position → name)
 * under department and position headings, keeping the API order.
 */
export function groupEmployees(
  employees: PlanBoardEmployee[],
  model: BoardModel,
): DepartmentGroup[] {
  const departments: DepartmentGroup[] = [];
  const deptByKey = new Map<string, DepartmentGroup>();
  const posByKey = new Map<string, PositionGroup>();

  for (const e of employees) {
    const deptKey = e.departmentId || e.departmentName || '__none__';
    let dept = deptByKey.get(deptKey);
    if (!dept) {
      dept = { key: deptKey, departmentName: e.departmentName || 'Bez działu', positions: [] };
      deptByKey.set(deptKey, dept);
      departments.push(dept);
    }
    const posKey = `${deptKey}::${e.positionId || '__none__'}`;
    let pos = posByKey.get(posKey);
    if (!pos) {
      const bySlot = e.positionId ? model.headcount.get(e.positionId) : undefined;
      const headcountSlots = bySlot
        ? sortSlots(
            Array.from(bySlot.keys())
              .map((id) => model.slotById.get(id))
              .filter((s): s is PlanSlotInfo => !!s),
          )
        : [];
      pos = {
        key: posKey,
        positionId: e.positionId ?? null,
        positionName: e.positionName || 'Bez stanowiska',
        employees: [],
        headcountSlots,
      };
      posByKey.set(posKey, pos);
      dept.positions.push(pos);
    }
    pos.employees.push(e);
  }
  return departments;
}
